import React, { useEffect, useRef, useState } from 'react';

import drawFallingConfetti from './util/drawFallingConfetti';
import AnimationProgress from './util/AnimationProgress';
import buildSprites from './util/buildSprites';

import { NUMBER_OF_CONFETTI, DURATION, COLORS } from './util/constants';

import { Canvas } from './Confetti.styles';

export type ConfettiProps = {
  element?: HTMLElement;
  confettiAmount?: number;
  yRange?: number;
  duration?: number;
  colors?: string[];
};

/**
 * ```import { Confetti } from '@zola/zola-ui/src/components/Confetti';```
 *
 * Renders a Confetti explosion in the specified container. If no container is specified, it defaults to ```document.body```. After ```duration``` param has elapsed, ```<Confetti />``` removes itself from the DOM by returning ```null```.
 *
 * The container, the amount of confetti, the vertical range of where the confetti falls, the duration of the animation, and the colors are all overwritable.
 */
const Confetti = ({
  element = document.body,
  confettiAmount = NUMBER_OF_CONFETTI,
  yRange = window.innerHeight + 200, // sometimes some confetti doesn't fall quite far enough before getting removed from the page, so adding 200 just helps a little bit
  duration = DURATION,
  colors = COLORS,
}: ConfettiProps): JSX.Element | null => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const width = element.offsetWidth;
  const height = element.offsetHeight;
  const progress = new AnimationProgress({ duration });
  const [durationElapsed, setDurationElapsed] = useState(false);
  const confetti = buildSprites({ confettiAmount, width, yRange, colors });

  useEffect(() => {
    // removes self from DOM after animation has run
    const timer = setTimeout(() => setDurationElapsed(true), duration);
    return () => clearTimeout(timer);
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    let animationFrameId: number;

    if (canvas) {
      const ctx = canvas.getContext('2d');
      canvas.width = width;
      canvas.height = height;

      const render = () => {
        drawFallingConfetti({ ctx, width, height, confettiAmount, confetti, yRange, progress });
        animationFrameId = window.requestAnimationFrame(render);
      };
      render();
      progress.start(performance.now());
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [confetti, confettiAmount, height, width, progress, yRange]);

  if (durationElapsed) return null;

  return <Canvas ref={canvasRef} />;
};

export default Confetti;
